import { Fragment, type ReactNode } from 'react';
import { ChevronDown, ChevronLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { LogoMark, LogoWordmark } from './Logo';

export interface NavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
  open: boolean;
  onToggle: () => void;
  icon?: ReactNode;
}

export type ContextEntry = NavItem | { label: string; icon?: ReactNode; isLabel: true };

export interface SidebarContext {
  /** The nav group the context belongs under. Absent renders it after every group. */
  groupKey?: string;
  kindLabel: string;
  name: string;
  backTo: string;
  backLabel: string;
  entries: ContextEntry[];
}

export interface SidebarProps {
  groups: NavGroup[];
  context?: SidebarContext;
  collapsed: boolean;
  onToggleCollapse: () => void;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  ariaLabel: string;
  labels: {
    collapse: string;
    expand: string;
  };
}

function isLabelEntry(entry: ContextEntry): entry is { label: string; icon?: ReactNode; isLabel: true } {
  return 'isLabel' in entry;
}

export function Sidebar({
  groups,
  context,
  collapsed,
  onToggleCollapse,
  drawerOpen,
  onCloseDrawer,
  ariaLabel,
  labels,
}: SidebarProps) {
  const railed = collapsed;
  const collapseLabel = collapsed ? labels.expand : labels.collapse;

  const contextBlock = context && (
            <div
              className={cn(
                'mt-3 flex flex-col gap-0.5 rounded-xl border border-neutral-200 bg-neutral-100 p-1.5',
                collapsed && 'mx-0.5 px-0.5',
              )}
            >
              <NavLink
                to={context.backTo}
                title={context.backLabel}
                aria-label={context.backLabel}
                className="flex items-center gap-2 rounded-lg px-2 pb-2 pt-1 text-left hover:bg-white dark:hover:bg-neutral-200"
              >
                {collapsed ? (
                  <ChevronLeft size={16} className="mx-auto text-brand-600" />
                ) : (
                  <>
                    <ChevronLeft size={14} className="shrink-0 text-brand-600" />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-neutral-400">
                        {context.kindLabel}
                      </span>
                      <span className="truncate text-xs font-bold text-neutral-900">
                        {context.name}
                      </span>
                    </span>
                  </>
                )}
              </NavLink>

              {context.entries.map((entry) =>
                isLabelEntry(entry) ? (
                  railed ? (
                    <span
                      key={entry.label}
                      role="img"
                      aria-label={entry.label}
                      title={entry.label}
                      className="mt-1.5 flex h-7 items-center justify-center border-t border-neutral-200 text-neutral-400"
                    >
                      {entry.icon}
                    </span>
                  ) : (
                    <span
                      key={entry.label}
                      className="whitespace-nowrap px-2 pb-0.5 pt-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-400"
                    >
                      {entry.label}
                    </span>
                  )
                ) : (
                  <SidebarItem key={entry.to} item={entry} collapsed={collapsed} onSurface />
                ),
              )}
            </div>
  );

  return (
    <>
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-media-scrim/50 lg:hidden"
          onClick={onCloseDrawer}
          aria-hidden
        />
      )}

      <nav
        aria-label={ariaLabel}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-neutral-200 bg-white transition-transform duration-200 dark:bg-neutral-100',
          drawerOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          'lg:translate-x-0 lg:shadow-none lg:transition-[width]',
          collapsed ? 'w-16' : 'w-56',
        )}
      >
        <div
          className={cn(
            'flex min-h-16 shrink-0 items-center gap-2.5 border-b border-neutral-200 px-4',
            collapsed && 'flex-col justify-center gap-2 px-0 py-3',
          )}
        >
          {collapsed ? <LogoMark /> : <LogoWordmark className="min-w-0 flex-1 truncate" />}
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapseLabel}
            title={collapseLabel}
            className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100 lg:inline-flex"
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {groups.map((group) => (
            <Fragment key={group.key}>
              {railed ? (
                <span
                  role="img"
                  aria-label={group.label}
                  title={group.label}
                  className="mt-2 flex h-8 shrink-0 items-center justify-center border-t border-neutral-200 text-neutral-400"
                >
                  {group.icon}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={group.onToggle}
                  aria-expanded={group.open}
                  className="flex w-full items-center justify-between whitespace-nowrap px-3 pb-1 pt-3.5 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-400 hover:text-neutral-600"
                >
                  {group.label}
                  <ChevronDown
                    size={12}
                    aria-hidden
                    className={cn('transition-transform', !group.open && '-rotate-90')}
                  />
                </button>
              )}

              {(group.open || railed) &&
                group.items.map((item) => (
                  <SidebarItem key={item.to} item={item} collapsed={collapsed} />
                ))}
              {context?.groupKey === group.key && contextBlock}
            </Fragment>
          ))}

          {context && !groups.some((group) => group.key === context.groupKey) && contextBlock}
        </div>
      </nav>
    </>
  );
}

function SidebarItem({
  item,
  collapsed,
  onSurface = false,
}: {
  item: NavItem;
  collapsed: boolean;
  onSurface?: boolean;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-[13px] transition-colors',
          isActive
            ? 'bg-brand-50 font-bold text-brand-800 dark:bg-brand-900 dark:text-brand-100'
            : cn(
                'text-neutral-800',
                onSurface ? 'hover:bg-white dark:hover:bg-neutral-200' : 'hover:bg-neutral-100',
              ),
          isActive && collapsed && 'shadow-[inset_3px_0_0_rgb(var(--color-brand-600))]',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <span className="inline-flex w-[18px] shrink-0 justify-center">{item.icon}</span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
    </NavLink>
  );
}
